import type { FC } from 'react';
import { CheckIcon, CopyIcon } from 'lucide-react';
import { cn } from '../../lib/cn';
import { useCopiedFlash } from '../../hooks/useCopiedFlash';

// Copy-to-clipboard hook (used by CodeHeader)

const useCopyToClipboard = ({
  copiedDuration = 3000,
}: {
  copiedDuration?: number;
} = {}) => {
  const [isCopied, flashCopied] = useCopiedFlash(copiedDuration);

  const copyToClipboard = (value: string) => {
    if (!value) return;
    void navigator.clipboard.writeText(value).then(() => {
      flashCopied();
    });
  };

  return { isCopied, copyToClipboard };
};

// CodeHeader (rendered above fenced code blocks)

export const CodeHeader: FC<{ language: string | undefined; code: string }> = ({
  language,
  code,
}) => {
  const { isCopied, copyToClipboard } = useCopyToClipboard();
  const onCopy = () => {
    if (!code || isCopied) return;
    copyToClipboard(code);
  };

  return (
    <div className="aui-code-header-root mt-2.5 flex items-center justify-between rounded-t-lg border border-stone-200/50 dark:border-nightshade-700/50 border-b-0 bg-stone-100/50 dark:bg-nightshade-800/50 px-3 py-1.5 text-xs">
      <span className="aui-code-header-language font-medium text-stone-500 dark:text-nightshade-400 lowercase">
        {language}
      </span>
      <button
        onClick={onCopy}
        className="inline-flex items-center justify-center size-6 rounded hover:bg-stone-200/60 dark:hover:bg-nightshade-700/60 text-stone-400 dark:text-nightshade-500 hover:text-stone-600 dark:hover:text-nightshade-300 transition-colors"
        aria-label="Copy code"
      >
        {!isCopied && <CopyIcon className="size-3.5" />}
        {isCopied && <CheckIcon className="size-3.5" />}
      </button>
    </div>
  );
};

// Shared markdown component overrides
//
// These work with both react-markdown and assistant-ui's MarkdownTextPrimitive.
// The `code` component here renders inline-code style unconditionally.
// For assistant-ui contexts, MarkdownText.tsx layers on useIsMarkdownCodeBlock
// to distinguish inline code from fenced code blocks.

export const markdownComponents = {
  h1: ({ className, ...props }: React.ComponentProps<'h1'>) => (
    <h1
      className={cn(
        'aui-md-h1 mb-2 scroll-m-20 font-semibold text-base first:mt-0 last:mb-0',
        className,
      )}
      {...props}
    />
  ),
  h2: ({ className, ...props }: React.ComponentProps<'h2'>) => (
    <h2
      className={cn(
        'aui-md-h2 mt-3 mb-1.5 scroll-m-20 font-semibold text-sm first:mt-0 last:mb-0',
        className,
      )}
      {...props}
    />
  ),
  h3: ({ className, ...props }: React.ComponentProps<'h3'>) => (
    <h3
      className={cn(
        'aui-md-h3 mt-2.5 mb-1 scroll-m-20 font-semibold text-sm first:mt-0 last:mb-0',
        className,
      )}
      {...props}
    />
  ),
  h4: ({ className, ...props }: React.ComponentProps<'h4'>) => (
    <h4
      className={cn(
        'aui-md-h4 mt-2 mb-1 scroll-m-20 font-medium text-sm first:mt-0 last:mb-0',
        className,
      )}
      {...props}
    />
  ),
  h5: ({ className, ...props }: React.ComponentProps<'h5'>) => (
    <h5
      className={cn(
        'aui-md-h5 mt-2 mb-1 font-medium text-sm first:mt-0 last:mb-0',
        className,
      )}
      {...props}
    />
  ),
  h6: ({ className, ...props }: React.ComponentProps<'h6'>) => (
    <h6
      className={cn(
        'aui-md-h6 mt-2 mb-1 font-medium text-sm first:mt-0 last:mb-0',
        className,
      )}
      {...props}
    />
  ),
  p: ({ className, ...props }: React.ComponentProps<'p'>) => (
    <p
      className={cn(
        'aui-md-p my-2.5 leading-normal first:mt-0 last:mb-0',
        className,
      )}
      {...props}
    />
  ),
  a: ({ className, ...props }: React.ComponentProps<'a'>) => (
    <a
      className={cn(
        'aui-md-a text-matcha-600 dark:text-matcha-400 underline underline-offset-2 hover:text-matcha-500 dark:hover:text-matcha-300',
        className,
      )}
      {...props}
    />
  ),
  blockquote: ({ className, ...props }: React.ComponentProps<'blockquote'>) => (
    <blockquote
      className={cn(
        'aui-md-blockquote my-2.5 border-stone-300 dark:border-nightshade-600 border-l-2 pl-3 text-stone-500 dark:text-nightshade-400 italic',
        className,
      )}
      {...props}
    />
  ),
  ul: ({ className, ...props }: React.ComponentProps<'ul'>) => (
    <ul
      className={cn(
        'aui-md-ul my-2 ml-4 list-disc marker:text-stone-400 dark:marker:text-nightshade-500 [&>li]:mt-1',
        className,
      )}
      {...props}
    />
  ),
  ol: ({ className, ...props }: React.ComponentProps<'ol'>) => (
    <ol
      className={cn(
        'aui-md-ol my-2 ml-4 list-decimal marker:text-stone-400 dark:marker:text-nightshade-500 [&>li]:mt-1',
        className,
      )}
      {...props}
    />
  ),
  hr: ({ className, ...props }: React.ComponentProps<'hr'>) => (
    <hr
      className={cn('aui-md-hr my-2 border-stone-200 dark:border-nightshade-700', className)}
      {...props}
    />
  ),
  table: ({ className, ...props }: React.ComponentProps<'table'>) => (
    <table
      className={cn(
        'aui-md-table my-2 w-full border-separate border-spacing-0 overflow-y-auto',
        className,
      )}
      {...props}
    />
  ),
  th: ({ className, ...props }: React.ComponentProps<'th'>) => (
    <th
      className={cn(
        'aui-md-th bg-stone-100 dark:bg-nightshade-800 px-2 py-1 text-left font-medium first:rounded-tl-lg last:rounded-tr-lg [[align=center]]:text-center [[align=right]]:text-right',
        className,
      )}
      {...props}
    />
  ),
  td: ({ className, ...props }: React.ComponentProps<'td'>) => (
    <td
      className={cn(
        'aui-md-td border-stone-200 dark:border-nightshade-700 border-b border-l px-2 py-1 text-left last:border-r [[align=center]]:text-center [[align=right]]:text-right',
        className,
      )}
      {...props}
    />
  ),
  tr: ({ className, ...props }: React.ComponentProps<'tr'>) => (
    <tr
      className={cn(
        'aui-md-tr m-0 border-b p-0 first:border-t [&:last-child>td:first-child]:rounded-bl-lg [&:last-child>td:last-child]:rounded-br-lg',
        className,
      )}
      {...props}
    />
  ),
  li: ({ className, ...props }: React.ComponentProps<'li'>) => (
    <li className={cn('aui-md-li leading-normal', className)} {...props} />
  ),
  sup: ({ className, ...props }: React.ComponentProps<'sup'>) => (
    <sup
      className={cn('aui-md-sup [&>a]:text-xs [&>a]:no-underline', className)}
      {...props}
    />
  ),
  pre: ({ className, ...props }: React.ComponentProps<'pre'>) => (
    <pre
      className={cn(
        'aui-md-pre overflow-x-auto rounded-t-none rounded-b-lg border border-stone-200/50 dark:border-nightshade-700/50 border-t-0 bg-stone-50 dark:bg-nightshade-800 p-3 text-xs leading-relaxed',
        className,
      )}
      {...props}
    />
  ),
  code: ({ className, ...props }: React.ComponentProps<'code'>) => (
    <code
      className={cn(
        'aui-md-inline-code rounded-md border border-stone-200/50 dark:border-nightshade-700/50 bg-stone-100/50 dark:bg-nightshade-800/50 px-1.5 py-0.5 font-mono text-[0.85em]',
        className,
      )}
      {...props}
    />
  ),
} as const;
